import type { MouseEvent, ReactNode, Ref } from "react";
import { Alert } from "./alert";
import { Badge } from "./badge";
import { initialen } from "./formular/auswahl-konfiguration";
import { Kontrollkaestchen } from "./formular/schalter";
import { Knopf } from "./knopf";
import { Fortschrittsring } from "./fortschrittsring";
import { Zustandspille } from "./zustandspille";
import { PersonenKarte, type PersonenKartenDaten } from "./personen-karte";

/**
 * Teilaufgaben-Zeile (Musterkatalog M-011 AD4, Design-Guide `teilaufgaben-zeile.md`): Haken, Text, rechts der Meta-Cluster
 * (Termin, Person). Hakenaufgaben schalten per Kontrollkästchen; Prozent- und Zähler-Teilaufgaben zeigen einen kleinen
 * Ring mit Zahl. Teil 2a (Pflege): Der Aufrufer kann über Plätze (`griff`, `aktionen`, `unten`) das Umsortieren, Stift und
 * Löschen sowie den Bereich unter der Zeile (Schieber, Bearbeiten, Rückfrage) einhängen, und über `onFortschrittKlick`
 * wird der Ring zum Knopf, der den Schieber öffnet. Gelöschte Zeilen (`t.geloescht`) sind grau und durchgestrichen,
 * tragen die Marke „gelöscht“ und bieten „Wiederherstellen“. Der Baustein prüft keine Rechte: ohne Schreibrecht übergibt der
 * Aufrufer `kannAendern=false` und keinen der Plätze. Ein Fehlschlag der Änderung steht als Satz an der Zeile (`fehler`,
 * r105), nicht als Toast.
 */
export interface TeilaufgabeAnzeige {
  id: string;
  text: string;
  art: "check" | "pct" | "count";
  erledigt: boolean;
  fortschritt: number;
  zaehlerText: string | null;
  /** Fertig formatiertes Datum („03.11.“). */
  faelligText: string | null;
  ueberfaellig: boolean;
  personName: string | null;
  /**
   * Angaben für die Personen-Karte (Name, Rolle, aktiv; sonst nichts). Gesetzt = der Avatar öffnet die Karte; `null`/fehlend
   * (E-Mail-artiger Name, „Unbekannt“) = Avatar bleibt ohne Karte wie bisher.
   */
  person?: PersonenKartenDaten | null;
  /** Lokal gelöscht (Wiederherstellen möglich). */
  geloescht?: boolean;
}

const RING_KNOPF =
  "shrink-0 inline-flex rounded-full focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none";

/** Ansage des Standes für den Ring-Knopf: Zähler als „N von M“, Prozent als „N %“ (nie „%“ bei einem Zähler). */
export function ringAnsage(t: Pick<TeilaufgabeAnzeige, "art" | "fortschritt" | "zaehlerText">): string {
  const zaehler = t.art === "count" ? /^(\d+)\/(\d+)$/.exec(t.zaehlerText ?? "") : null;
  return zaehler ? `${zaehler[1]} von ${zaehler[2]}` : `${t.fortschritt} %`;
}

export function TeilaufgabenZeile({
  t,
  kannAendern,
  laeuft = false,
  fehler,
  onUmschalten,
  ohneHaekchen = false,
  ohneDurchstreichen = false,
  griff,
  aktionen,
  unten,
  onFortschrittKlick,
  onNameKlick,
  nameOffen = false,
  nameSteuert,
  fortschrittOffen = false,
  fortschrittSteuert,
  onWiederherstellen,
  liRef,
  hervorgehoben = false,
  gezogen = false,
}: {
  t: TeilaufgabeAnzeige;
  kannAendern: boolean;
  laeuft?: boolean;
  fehler?: string;
  /** Pflicht, solange die Zeile ein Kästchen trägt (`ohneHaekchen` aus). */
  onUmschalten?: (erledigt: boolean) => void;
  /**
   * Hakenzeile ohne Kontrollkästchen (Vorbereiten-Agenda, Betreiber 13.10.2026): nur der Name, ein erledigter Punkt trägt
   * lesend die Marke „Erledigt“. Wirkt nur bei `art="check"`.
   */
  ohneHaekchen?: boolean;
  /**
   * Haken bleibt, der Text bleibt unverändert (nicht grau, nicht durchgestrichen). Für Zeilen, deren Haken keine „erledigt, weg damit“-Aussage trägt,
   * sondern einen Nachweis (Akzeptanzkriterien: „aus Sicht Umsetzung erfüllt“, Betreiber 17.10.2026; Design-Guide `teilaufgaben-zeile.md`).
   */
  ohneDurchstreichen?: boolean;
  /** Platz vor der Kontrolle: Griff zum Umsortieren (nur mit Schreibrecht). */
  griff?: ReactNode;
  /** Platz hinter dem Meta-Cluster: Stift, Löschen. */
  aktionen?: ReactNode;
  /** Bereich unter der Zeile: Schieber, Bearbeiten, Rückfrage. */
  unten?: ReactNode;
  /** Der Ring wird zum Knopf (Prozent/Zähler, nur mit Schreibrecht). */
  onFortschrittKlick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Der Name einer Hakenzeile wird zum Knopf, der Details öffnet (Betreiber 12.10.2026: Kästchen hakt ab, Name öffnet
   * Details). Ohne Handler ist der Name reiner Text und KEIN Klickziel; abhaken tut er in keinem Fall (kein `<label>`).
   */
  onNameKlick?: (e: MouseEvent<HTMLButtonElement>) => void;
  nameOffen?: boolean;
  /** Id des Bereichs, den der Namen-Knopf öffnet (`aria-controls`). */
  nameSteuert?: string;
  fortschrittOffen?: boolean;
  /** Id des Bereichs, den der Ring-Knopf öffnet (`aria-controls`). */
  fortschrittSteuert?: string;
  /** Nur bei gelöschten Zeilen: „Wiederherstellen“. */
  onWiederherstellen?: () => void;
  liRef?: Ref<HTMLLIElement>;
  /** Ziel des Umsortierens (Rand oben in Primärfarbe). */
  hervorgehoben?: boolean;
  /** Die Zeile wird gerade gezogen. */
  gezogen?: boolean;
}) {
  const geloescht = Boolean(t.geloescht);
  const ring = <Fortschrittsring prozent={t.fortschritt} label={`Fortschritt von „${t.text}“`} wertText={ringAnsage(t)} />;
  const grau = (t.erledigt && !ohneHaekchen && !ohneDurchstreichen) || geloescht;
  // `block`: ein Inline-Span im Kästchen-Block säße auf der 24-px-Grundlinie des Elternblocks (text-base) und läge 2 px unter
  // der Kästchen-Mitte; als Blockelement hat die Zeile genau 20 px (text-sm), wie im Preline-Muster (Flex-Item).
  const nameKlasse = grau ? "block text-sm text-muted-foreground-1 line-through" : "block text-sm text-foreground";
  // Name als Knopf: 44 px Zielhöhe über `py-3` + eine 20-px-Zeile (items-start: die erste Zeile bleibt bei 12 px, auch bei
  // Umbruch); das Kästchen sitzt dazu per `beschriftungHoehe="beruehrung"` (mt-3.5) mittig zur ersten Zeile.
  const knopfKlasse = grau
    ? "text-sm text-muted-foreground-1 line-through inline-flex items-start py-3 text-start rounded-sm hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus"
    : "text-sm text-foreground inline-flex items-start py-3 text-start rounded-sm hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
  const name =
    onNameKlick && !geloescht ? (
      <button
        type="button"
        className={knopfKlasse}
        aria-expanded={nameOffen}
        // `aria-controls` nur, wenn das Ziel im DOM steht (der Bereich steht nur offen im DOM).
        aria-controls={nameOffen ? nameSteuert : undefined}
        onClick={(e) => onNameKlick(e)}
      >
        {t.text}
      </button>
    ) : (
      <span className={nameKlasse}>{t.text}</span>
    );
  const klasseLi = [
    // `group/zeile`: Hover über der Zeile zeigt leise Plätze des Aufrufers (Nachrichten-Knopf ohne Nachrichten, W205 T-A); benannte Gruppe,
    // damit `group-hover` anderer Bausteine in der Zeile nicht anspringt.
    "group/zeile py-2.5 first:pt-0 last:pb-0",
    // Haarlinie zwischen den Zeilen (Betreiber-Entscheid 08.10.2026, 1B): nicht über der ersten, nicht unter der letzten.
    hervorgehoben ? "border-t-2 border-primary" : "border-t border-line-2 first:border-t-0",
    gezogen ? "opacity-50" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <>
      {/* Preline: dashboards/admin-dashboard/to-do.html#task-list (Datei: templates/dashboards/admin-dashboard/to-do.html, Tab-Panel hs-pro-tabs-dtdt-today, Abschnitt „To do“, Karten ab Label hs-pro-tdlrtd1; „#task-list“ ist ein sprechender Bereichsname, keine id im Template) */}
      <li ref={liRef} className={klasseLi}>
        {/* Gelöschte Zeile: unter sm bricht die Meta-Zeile (Marke + Wiederherstellen) unter den Titel um (kein Überlappen mit dem Zähler); ab sm unverändert in einer Zeile. */}
        <div className={geloescht ? "flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-3 gap-y-1.5" : "flex items-center justify-between gap-x-3"}>
          {griff && <div className="shrink-0">{griff}</div>}
          <div className="min-w-0 grow">
            {t.art === "check" && ohneHaekchen ? (
              <div className="py-1">{name}</div>
            ) : t.art === "check" ? (
              <Kontrollkaestchen
                label={t.text}
                beschriftung={name}
                beschriftungHoehe={onNameKlick && !geloescht ? "beruehrung" : "zeile"}
                checked={t.erledigt}
                disabled={!kannAendern || laeuft || geloescht}
                durchgestrichen={(t.erledigt && !ohneDurchstreichen) || geloescht}
                onChange={(e) => onUmschalten?.(e.target.checked)}
              />
            ) : (
              <div className="flex items-center gap-x-3">
                {onFortschrittKlick && !geloescht ? (
                  <button
                    type="button"
                    className={RING_KNOPF}
                    disabled={laeuft}
                    aria-expanded={fortschrittOffen}
                    // `aria-controls` nur, wenn das Ziel im DOM steht (der Bereich steht nur offen im DOM).
                    aria-controls={fortschrittOffen ? fortschrittSteuert : undefined}
                    aria-label={`${ringAnsage(t)} erledigt, Fortschritt ändern: ${t.text}`}
                    onClick={(e) => onFortschrittKlick(e)}
                  >
                    {ring}
                  </button>
                ) : (
                  ring
                )}
                <span className={t.erledigt || geloescht ? "min-w-0 text-sm text-muted-foreground-1 line-through" : "min-w-0 text-sm text-foreground"}>{t.text}</span>
                {/* Meta bleibt in einer Zeile („0 %“ bricht bei 375 px nicht um); der Titel darf umbrechen (Vorschau 5D). */}
                <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground-1 tabular-nums">{t.zaehlerText ?? `${t.fortschritt} %`}</span>
              </div>
            )}
          </div>
          <div className={geloescht ? "shrink-0 flex items-center gap-x-2 basis-full sm:basis-auto" : "shrink-0 flex items-center gap-x-2"}>
            {geloescht && <Badge ton="gedaempft">gelöscht</Badge>}
            {!geloescht && ohneHaekchen && t.art === "check" && t.erledigt && <Badge ton="erfolg">Erledigt</Badge>}
            {/* A4′ (Betreiber-Vorschlag 14.10.2026, am Bild zu bestätigen): unter `sm` statt der Pille ein roter Punkt mit rotem Datum (sr-only „überfällig“, Farbe nie allein); ab `sm` die Pille. */}
            {!geloescht && t.ueberfaellig && (
              <span className="hidden sm:inline-flex">
                <Zustandspille ton="ueberfaellig">Überfällig</Zustandspille>
              </span>
            )}
            {!geloescht && t.faelligText && t.ueberfaellig && (
              <>
                <span className="sm:hidden shrink-0 inline-flex items-center gap-x-1 whitespace-nowrap text-xs font-medium text-destructive tabular-nums">
                  <span className="size-2 inline-block rounded-full bg-current" aria-hidden="true" />
                  {t.faelligText}
                  <span className="sr-only">, überfällig</span>
                </span>
                <span className="hidden sm:inline shrink-0 whitespace-nowrap text-xs text-muted-foreground-1 tabular-nums">{t.faelligText}</span>
              </>
            )}
            {!geloescht && t.faelligText && !t.ueberfaellig && <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground-1 tabular-nums">{t.faelligText}</span>}
            {!geloescht && t.personName && (
              t.person ? (
                // Personen-Karte (Design-Guide personen-karte.md, Matrix T3 Teilaufgaben-Zeile): der Avatar ist der Auslöser.
                <PersonenKarte person={t.person}>
                  <span className="inline-flex shrink-0 items-center justify-center size-6 rounded-full bg-surface text-xs font-medium text-surface-foreground" title={t.personName}>
                    <span aria-hidden="true">{initialen(t.personName)}</span>
                    <span className="sr-only">Zugewiesen an {t.personName}, Personen-Karte öffnen</span>
                  </span>
                </PersonenKarte>
              ) : (
                <span
                  className="inline-flex shrink-0 items-center justify-center size-6 rounded-full bg-surface text-xs font-medium text-surface-foreground"
                  title={t.personName}
                >
                  <span aria-hidden="true">{initialen(t.personName)}</span>
                  <span className="sr-only">Zugewiesen an {t.personName}</span>
                </span>
              )
            )}
            {geloescht && onWiederherstellen && (
              <Knopf variante="text" disabled={laeuft} onClick={onWiederherstellen}>
                Wiederherstellen
              </Knopf>
            )}
            {!geloescht && aktionen}
          </div>
        </div>
        {fehler && (
          <div className="mt-1.5">
            <Alert ton="warnung">{fehler}</Alert>
          </div>
        )}
        {!geloescht && unten}
      </li>
    </>
  );
}
